"use client";
import {
  BodyText,
  breakpointMobile,
  breakpointTablet,
  GhostButton,
  InfoCard,
  PrimaryButton,
} from "@gnist/design-system";
import NextLink from "../../../../components/nextLink";
import React, { useEffect } from "react";
import { styled } from "styled-components";
import { Price } from "./Price";
import { Car } from "@/data/bruktbilsalgBackend/types/car.types";
import { CampaignContent } from "@/components/campaign/campaingContent";
import { useDealerCampaign } from "@/components/campaign/campaignProvider";
import { TradeInModal } from "@/components/tradeIn/tradeInModal";
import { useTradeInContext } from "@/components/tradeIn/tradeInProvider";
import { TradeInContent } from "@/components/tradeIn/tradeInContent";
import { datadogRum } from "@datadog/browser-rum";
import { tokens } from "@gnist/themes/tokens.css.js";

const CardWrapper = styled.div`
  > div {
    display: flex;
    flex-direction: column;
    background-color: ${tokens.color.surface};
    color: ${tokens.color["on-surface"]};

    @media screen and (max-width: ${breakpointMobile}) {
      align-items: center;
      justify-content: center;
      padding: ${tokens.spacing.m};
    }
  }
`;

const ContainerInfoCard = styled.div`
  display: flex;
  width: 100%;

  @media screen and (max-width: ${breakpointMobile}) {
    justify-content: center;
  }
`;

const Content = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  justify-content: space-between;
  flex: 1;
  align-items: center;
  gap: v ${tokens.spacing.xxl};

  @media screen and (max-width: ${breakpointMobile}) {
    flex: unset;
    flex-direction: column;
    align-items: start;
    width: 100%;
    gap: ${tokens.spacing.xxs};
  }
`;
const ButtonsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: ${tokens.spacing.xxs};
  column-gap: ${tokens.spacing.s};
  width: 100%;

  > * {
    width: 100%;
    box-sizing: border-box;
  }

  @media screen and (min-width: ${breakpointTablet}) {
    flex-direction: row;
    width: unset;

    > * {
      width: unset;
    }
  }
`;

const Leasing = styled(BodyText)`
  font-size: ${tokens.typeface.size.m};
  font-weight: ${tokens.typeface.weight.semibold};

  @media screen and (max-width: ${breakpointMobile}) {
    margin-bottom: ${tokens.spacing.xxs};
  }
`;

const Divider = styled.hr`
  display: flex;
  border: none;
  height: 0.5px;
  background-color: ${tokens.color.outline};
  width: calc(100% + calc(${tokens.spacing.l} * 2));
  margin: ${tokens.spacing.l} calc(${tokens.spacing.l} * -1);

  @media screen and (max-width: ${breakpointMobile}) {
    width: calc(100% + calc(${tokens.spacing.m} * 2));
    margin: ${tokens.spacing.m} calc(${tokens.spacing.m} * -1);
  }
`;

interface Props {
  car: Car;
  departmentSlug?: string;
}

export const PricingSection = ({ car, departmentSlug }: Props) => {
  const { dealerCampaign } = useDealerCampaign(car.departmentName);
  const { tradeInPrice, setIsShowingTradeInModal } = useTradeInContext();
  const isTradeInEnabled = !departmentSlug;
  const isDealerMoller = car.departmentName.toLowerCase().includes("møller");

  useEffect(() => {
    if (isTradeInEnabled && !tradeInPrice) {
      datadogRum.addAction("Sees trade-in button in car page");
    }
  }, [isTradeInEnabled, tradeInPrice]);

  return (
    <>
      <CardWrapper>
        <InfoCard>
          <ContainerInfoCard>
            <Content>
              {car.isLeasing ? (
                <Leasing>Leasing</Leasing>
              ) : (
                <Price
                  priceText={"TOTALPRIS"}
                  priceVal={car.price}
                  isSold={car.isSold}
                  isTradeInEnabled={isTradeInEnabled || false}
                />
              )}
              <ButtonsWrapper>
                {isTradeInEnabled && !tradeInPrice && (
                  <GhostButton
                    onClick={() => {
                      setIsShowingTradeInModal(true);
                      datadogRum.addAction(
                        "Clicks button to add trade-in car in car page",
                      );
                    }}
                  >
                    Legg til innbyttebil
                  </GhostButton>
                )}
                {car.isSold ? (
                  <PrimaryButton disabled>Avtal visning</PrimaryButton>
                ) : (
                  <PrimaryButton
                    $as={NextLink}
                    href={
                      departmentSlug
                        ? `/forhandler/${departmentSlug}/avtal-visning/${car.carId}`
                        : `/avtal-visning/${car.carId}`
                    }
                  >
                    Avtal visning
                  </PrimaryButton>
                )}
              </ButtonsWrapper>
            </Content>
          </ContainerInfoCard>
          {!!tradeInPrice && isTradeInEnabled && (
            <>
              <Divider />
              <TradeInContent
                showDesktopSwitchLabel
                context="car"
                isDealerMoller={isDealerMoller}
              />
            </>
          )}
          {dealerCampaign && (
            <>
              <Divider />
              <CampaignContent
                campaign={dealerCampaign}
                departmentSlug={departmentSlug}
              />
            </>
          )}
        </InfoCard>
      </CardWrapper>
      <TradeInModal context="car" isDealerMoller={isDealerMoller} />
    </>
  );
};
